/**
 * Encryption utilities for secure storage of user data
 * Uses Web Crypto API with AES-GCM encryption
 */

const ENCRYPTION_KEY = process.env.REACT_APP_ENCRYPTION_KEY || 'default-dev-key-change-in-production';

/**
 * Derives a CryptoKey from the environment secret
 */
async function deriveKey() {
    const encoder = new TextEncoder();
    const keyMaterial = await crypto.subtle.importKey(
        'raw',
        encoder.encode(ENCRYPTION_KEY),
        { name: 'PBKDF2' },
        false,
        ['deriveKey']
    );

    return crypto.subtle.deriveKey(
        {
            name: 'PBKDF2',
            salt: encoder.encode('capt-nexus-salt'),
            iterations: 100000,
            hash: 'SHA-256'
        },
        keyMaterial,
        { name: 'AES-GCM', length: 256 },
        false,
        ['encrypt', 'decrypt']
    );
}

/**
 * Encrypts a user ID for secure storage in localStorage
 * @param {string} userId - The user ID to encrypt
 * @returns {Promise<string>} - Base64 encoded encrypted data (iv:ciphertext)
 */
export async function encryptUserId(userId) {
    try {
        const key = await deriveKey();
        const encoder = new TextEncoder();
        const iv = crypto.getRandomValues(new Uint8Array(12));

        const encrypted = await crypto.subtle.encrypt(
            { name: 'AES-GCM', iv },
            key,
            encoder.encode(userId)
        );

        // Combine IV and ciphertext, encode as base64
        const combined = new Uint8Array(iv.length + encrypted.byteLength);
        combined.set(iv);
        combined.set(new Uint8Array(encrypted), iv.length);

        return btoa(String.fromCharCode(...combined));
    } catch (error) {
        console.error('Encryption failed:', error);
        throw error;
    }
}

/**
 * Decrypts an encrypted user ID from localStorage
 * @param {string} encryptedData - Base64 encoded encrypted data
 * @returns {Promise<string|null>} - The decrypted user ID or null if failed
 */
export async function decryptUserId(encryptedData) {
    try {
        if (!encryptedData) return null;

        const key = await deriveKey();
        const decoder = new TextDecoder();

        // Decode base64 and extract IV and ciphertext
        const combined = Uint8Array.from(atob(encryptedData), c => c.charCodeAt(0));
        const iv = combined.slice(0, 12);
        const ciphertext = combined.slice(12);

        const decrypted = await crypto.subtle.decrypt(
            { name: 'AES-GCM', iv },
            key,
            ciphertext
        );

        return decoder.decode(decrypted);
    } catch (error) {
        console.error('Decryption failed:', error);
        return null;
    }
}

/**
 * Store encrypted user ID in localStorage
 * @param {string} userId - The user ID to store
 */
export async function storeEncryptedUserId(userId) {
    const encrypted = await encryptUserId(userId);
    localStorage.setItem('user_id', encrypted);
}

/**
 * Retrieve and decrypt user ID from localStorage
 * @returns {Promise<string|null>} - The decrypted user ID or null
 */
export async function getDecryptedUserId() {
    const encrypted = localStorage.getItem('user_id');
    if (!encrypted) return null;
    return decryptUserId(encrypted);
}

/**
 * Clear stored user ID
 */
export function clearStoredUserId() {
    localStorage.removeItem('user_id');
}
